import React from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import FactTemplateForm from "pages/FactTemplate/Form";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {FactTemplateData} from "types/FactTemplate";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";

function MutateFactTemplate() {
  const {id} = useParams()
  const {data: fact, isLoading} = useQuery(
    ['fact-templates', {id}],
    () => ngage.factTemplates.get(parseInt(id as string)),
    {enabled: !!id}
  )

  const store = useMutation((data: FactTemplateData) => ngage.factTemplates.store(data))
  const update = useMutation((data: FactTemplateData) => ngage.factTemplates.update(parseInt(id as string), data))

  const {termFor} = useOperator()
  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Handle form submission
  async function handleSubmit(values: FactTemplateData, {setSubmitting}: FormikHelpers<FactTemplateData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('fact-templates.all')
    await invalidate('fact-templates')

    setSubmitting(false)
    navigate('/fact-templates')
  }

  // Handle store mutation
  async function handleStore(values: FactTemplateData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Fact')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Fact')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: FactTemplateData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Fact')} Updated`, `${values.name} has been updated`)
      await invalidate(['facts', {id}])
    } catch (error) {
      notify("error", `${termFor('Fact')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prepare breadcrumbs
  const breadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Facts')]: '/fact-templates',
    [`${id ? 'Edit' : 'New'} ${termFor('Fact')}`]: `${id ? `/fact-templates/${id}/edit` : '/fact-templates/new'}`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Fact')}`}
      breadcrumbs={breadcrumbs}
      isLoading={isLoading}
    >
      <FactTemplateForm factTemplate={fact} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateFactTemplate;